import React, { FC, memo, useCallback, useEffect, useRef } from "react";
import { setInitializing, setProcessing, setReady } from "@store/avatarSlice";
import { AzureAvatarConfig } from "@models/avatar-config";
import styled from "@emotion/styled";
import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import useStreaming from "@components/AzureAvatar/useStreaming";
import useBatching from "@components/AzureAvatar/useBatching";
import { useDispatch, useSelector } from "react-redux";
import { useRecordVoice } from "@hooks/useRecordVoice";
import { RootState } from "@store/store";
import { EventType } from "@models/audio-message";
import { useNavigate } from "react-router-dom";
import { setMessage } from "@store/snackbarSlice";
import { useTranslation } from "react-i18next";

const Video = styled.video`
  width: 100%;
  height: 100%;
`;

export interface AzureAvatarProps {
  sessionId: string;
  photoId?: string;
  avatarConfig: AzureAvatarConfig;
  streaming: boolean;
  companionName: string;
}

const AzureAvatar: FC<AzureAvatarProps> = ({
  sessionId,
  photoId,
  avatarConfig,
  streaming,
  companionName,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const avatarRef = useRef<AzureAvatarService | null>(null);
  const dispatch = useDispatch();
  const processing = useSelector((state: RootState) => state.avatar.processing);
  const photoIdRef = useRef<string | undefined>();
  const navigate = useNavigate();
  const isPatient = useSelector(
    (state: RootState) => state.auth.user?.isPatient,
  );
  const { t } = useTranslation();

  const sendMessageStreaming = useStreaming({
    sessionId,
    avatar: avatarRef,
  });

  const sendMessageBatching = useBatching({
    sessionId,
    avatar: avatarRef,
  });

  const sendMessage = useCallback(
    (
      audio?: Blob | ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ) => {
      streaming
        ? sendMessageStreaming(audio as ArrayBufferLike, photoId, eventType)
        : sendMessageBatching(audio as Blob, photoId, eventType);
    },
    [sendMessageBatching, sendMessageStreaming, streaming],
  );

  useRecordVoice(
    processing,
    (audio) => sendMessage(audio, photoIdRef.current, EventType.UserInput),
    streaming,
  );

  const startAvatar = async (avatar: AzureAvatarService) => {
    dispatch(setInitializing(true));
    try {
      await avatar.start(
        avatarConfig,
        videoRef.current!,
        audioRef.current!,
        sessionId,
      );
      if (avatar.running && !avatar.stopping) {
        dispatch(setInitializing(false));
        dispatch(setReady(true));
        sendMessage(undefined, photoId, EventType.NewConversation);
      }
    } catch (e) {
      dispatch(
        setMessage({
          content: t(`errors.avatarConnectionFailed`, { name: companionName }),
          severity: "error",
        }),
      );
      navigate(isPatient ? "/portal" : "/");
    }
  };

  useEffect(() => {
    if (photoId && photoIdRef.current && photoId !== photoIdRef.current) {
      sendMessage(undefined, photoId, EventType.NewPhoto);
    }
    photoIdRef.current = photoId;
  }, [photoId, sendMessage]);

  useEffect(() => {
    dispatch(setProcessing(true));
    const avatar = new AzureAvatarService();
    avatarRef.current = avatar;
    startAvatar(avatar);

    return () => {
      avatar.stop();
      dispatch(setReady(false));
    };
  }, [avatarConfig, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Video autoPlay playsInline ref={videoRef} />
      <audio autoPlay playsInline ref={audioRef} />
    </>
  );
};

export default memo(AzureAvatar);
